import { computed, type Ref } from 'vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import * as api from './endpoints'
import type {
  SearchJudgmentOverviewParams,
  NgWordCheckInput,
  JudgeOffersRequest,
  SaveMemoRequest,
  UpdateOfferFlagsRequest,
  ApplyRenewalRequest,
  AddProductImageRequest,
  ManageBrowseNodeRequest,
  SaveMallCategoryRequest,
  MoveProductImageRequest,
  RefreshProductImageRequest,
  SaveProductDetailRequest,
  RefreshProductFromCatalogRequest,
  BulkDeleteImagesRequest,
  BulkReplaceImagesRequest,
  BulkAddSiblingImagesRequest,
  ApplyJudgmentQueueRequest,
  CreateUserRequest,
  UpdateUserRequest,
  SetUserStatusRequest,
  SaveProductImagesRequest,
} from '@/types/api'

/** クエリキーを一箇所にまとめておく(invalidateQueries時の指定ミスを防ぐ) */
export const queryKeys = {
  masters: ['masters'] as const,
  offers: (params: SearchJudgmentOverviewParams) => ['offers', params] as const,
  offerDetail: (asin: string, sellerId: string) => ['offerDetail', asin, sellerId] as const,
  productImages: (asin: string) => ['productImages', asin] as const,
  judgeLog: (asin: string) => ['judgeLog', asin] as const,
  mallCategory: (asin: string) => ['mallCategory', asin] as const,
  browseNodeMaster: (keyword: string) => ['browseNodeMaster', keyword] as const,
  mallCategoryList: (dbName: string, keyword: string) =>
    ['mallCategoryList', dbName, keyword] as const,
  productDetail: (asin: string, regionCode: string) => ['productDetail', asin, regionCode] as const,
  users: (keyword: string, group: string, status: string) => ['users', keyword, group, status] as const,
}

/** 1. 初期マスタ (アプリ起動時に1回、staleTimeを長めにしてほぼキャッシュのみ使う) */
export function useInitMasters() {
  return useQuery({
    queryKey: queryKeys.masters,
    queryFn: api.getInitMasters,
    staleTime: 1000 * 60 * 30, // 30分
  })
}

/** 2. 審査対象一覧検索 (paramsが変わるたびに自動で再取得される) */
export function useJudgmentOverview(params: Ref<SearchJudgmentOverviewParams>) {
  return useQuery({
    queryKey: computed(() => queryKeys.offers(params.value)),
    queryFn: () => api.searchJudgmentOverview(params.value),
  })
}

/** 3. + 4. + 5. 商品選択時の詳細一式（詳細・画像・NGワードを並行取得） */
export function useOfferDetail(asin: Ref<string | null>, sellerId: Ref<string | null>) {
  return useQuery({
    queryKey: computed(() => queryKeys.offerDetail(asin.value ?? '', sellerId.value ?? '')),
    queryFn: () => api.getOfferDetail(asin.value!, sellerId.value!),
    enabled: computed(() => !!asin.value && !!sellerId.value),
  })
}

// ---- 画像OCR NGワードチェック: 画像の追加・差し替え直後の自動再取得 ----------------
// 追加・差し替えた画像はOCRワーカー(1分ごとに起動)が処理するまで ocr.status='pending' になる。
// 画面を開き直さなくても結果が出るよう、次の条件をすべて満たす間だけ画像一覧を定期的に再取得する。
//   - この画面でその商品の画像を追加・差し替えてから OCR_POLL_WINDOW_MS 以内
//   - 表示中の画像に pending が残っている
//   - ブラウザのタブが表示されている(vue-query の既定動作)
// 初回表示は待たせず(通常どおり1回取得して表示)、裏で再取得するだけなので表示は遅くならない。
// 画像を触っていない商品は再取得しない(既存分のOCR未処理画像が多数あるため、
// 「pendingがあれば再取得」にすると大半の商品で再取得し続けてしまう)。
const OCR_POLL_INTERVAL_MS = 20_000
const OCR_POLL_WINDOW_MS = 5 * 60_000
const imagesEditedAt = new Map<string, number>()

/** 画像の追加・差し替えを記録し、その商品の画像一覧をOCR結果待ちの再取得対象にする */
function markImagesEdited(asin: string) {
  imagesEditedAt.set(asin, Date.now())
}

export function useProductImages(asin: Ref<string | null>) {
  return useQuery({
    queryKey: computed(() => queryKeys.productImages(asin.value ?? '')),
    queryFn: () => api.getProductImages(asin.value!),
    enabled: computed(() => !!asin.value),
    // 取得のたびに評価され、false を返した時点で再取得が止まる
    refetchInterval: (query) => {
      const key = asin.value
      const editedAt = key ? imagesEditedAt.get(key) : undefined
      if (!editedAt || Date.now() - editedAt > OCR_POLL_WINDOW_MS) return false
      const hasPending = query.state.data?.images.some((img) => img.ocr?.status === 'pending')
      return hasPending ? OCR_POLL_INTERVAL_MS : false
    },
  })
}

/** セルモン商品編集 [フェーズ1]: ASIN+国コードで商品情報を単独取得 */
export function useProductDetail(asin: Ref<string | null>, regionCode: Ref<string>) {
  return useQuery({
    queryKey: computed(() => queryKeys.productDetail(asin.value ?? '', regionCode.value)),
    queryFn: () => api.getProductDetail(asin.value!, regionCode.value),
    enabled: computed(() => !!asin.value),
  })
}

/** セルモン商品編集 [フェーズ2]: 手動編集した商品情報を保存 */
export function useSaveProductDetailMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: SaveProductDetailRequest) => api.saveProductDetail(req),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productDetail(variables.asin, variables.regionCode),
      })
      // 審査画面のモーダル(ProductEditModal)からも保存できるようになったため、
      // 審査画面側のキャッシュ(offerDetail)も無効化して、モーダルを閉じたときに
      // 更新後の商品名・特徴・商品説明が反映されるようにする。
      // sellerIdは持っていないため、asinを含む全offerDetailキーを対象に緩く無効化する。
      queryClient.invalidateQueries({
        queryKey: ['offerDetail'],
        predicate: (q) => q.queryKey.includes(variables.asin),
      })
    },
  })
}

/** セルモン商品編集 [フェーズ3]: Amazonカタログ(SP-API)から最新情報を取得 */
export function useRefreshProductFromCatalogMutation() {
  return useMutation({
    mutationFn: ({ asin, req }: { asin: string; req: RefreshProductFromCatalogRequest }) =>
      api.refreshProductFromCatalog(asin, req),
  })
}

/**
 * NGワード判定は「一覧行を選択するたびに自動実行」される仕様(README参照)なので、
 * GETキャッシュ的なuseQueryではなく、明示的に呼び出すuseMutationとして提供する。
 * (商品切り替え時と「NGワード更新」ボタンの両方から同じ関数を呼べる)
 */
export function useCheckNgWords() {
  return useMutation({
    mutationFn: (input: NgWordCheckInput) => api.checkNgWords(input),
  })
}

/** 6. 審査履歴（パネルを開いた時だけ呼ぶ想定なのでenabledで制御） */
export function useJudgeLog(asin: Ref<string | null>, enabled: Ref<boolean>) {
  return useQuery({
    queryKey: computed(() => queryKeys.judgeLog(asin.value ?? '')),
    queryFn: () => api.getJudgeLog(asin.value!),
    enabled: computed(() => !!asin.value && enabled.value),
  })
}

/** 7. 審査アクション。成功後は一覧・詳細・履歴を自動で再取得する */
export function useJudgeOffersMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: JudgeOffersRequest) => api.judgeOffers(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] })
      queryClient.invalidateQueries({ queryKey: ['offerDetail'] })
      queryClient.invalidateQueries({ queryKey: ['judgeLog'] })
    },
  })
}

/** 8. メモ保存 */
export function useSaveMemoMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ asin, req }: { asin: string; req: SaveMemoRequest }) => api.saveMemo(asin, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offerDetail'] })
    },
  })
}

/** 9. フラグ更新（削除・一時停止・無効化） */
export function useUpdateOfferFlagsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ asin, req }: { asin: string; req: UpdateOfferFlagsRequest }) =>
      api.updateOfferFlags(asin, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] })
      queryClient.invalidateQueries({ queryKey: ['offerDetail'] })
    },
  })
}

/** 10. 更新申請 */
export function useApplyRenewalMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ asin, req }: { asin: string; req: ApplyRenewalRequest }) =>
      api.applyRenewal(asin, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] })
      queryClient.invalidateQueries({ queryKey: ['offerDetail'] })
    },
  })
}

/** 11. モール別カテゴリ割当 */
export function useMallCategoryMapping(asin: Ref<string | null>) {
  return useQuery({
    queryKey: computed(() => queryKeys.mallCategory(asin.value ?? '')),
    queryFn: () => api.getMallCategoryMapping(asin.value!),
    enabled: computed(() => !!asin.value),
  })
}

/** 14. ブラウズノードマスタ検索(追加ダイアログ用) */
export function useBrowseNodeMaster(keyword: Ref<string>, enabled: Ref<boolean>) {
  return useQuery({
    queryKey: computed(() => queryKeys.browseNodeMaster(keyword.value)),
    queryFn: () => api.getBrowseNodeMaster(keyword.value || undefined),
    enabled: computed(() => enabled.value),
    // 入力のたびに叩くと重いので、少し古い結果を許容
    staleTime: 30_000,
  })
}

/** 15. ブラウズノード 追加/削除/兄弟へ反映 */
export function useManageBrowseNodeMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ asin, req }: { asin: string; req: ManageBrowseNodeRequest }) =>
      api.manageProductBrowseNode(asin, req),
    onSuccess: (_data, variables) => {
      // カテゴリ表示を最新化(ブラウズノードが変わるとカテゴリも変わるため)
      queryClient.invalidateQueries({ queryKey: queryKeys.mallCategory(variables.asin) })
      // 商品のEnableFlgが'2'に変わるので詳細も更新
      queryClient.invalidateQueries({ queryKey: ['offerDetail', variables.asin] })
    },
  })
}

/** 16. モールカテゴリマスタ検索(カテゴリ編集ダイアログ用) */
export function useMallCategoryList(
  dbName: Ref<string>,
  keyword: Ref<string>,
  enabled: Ref<boolean>,
) {
  return useQuery({
    queryKey: computed(() => queryKeys.mallCategoryList(dbName.value, keyword.value)),
    queryFn: () => api.getMallCategoryList(dbName.value, keyword.value || undefined),
    enabled: computed(() => enabled.value && !!dbName.value),
    staleTime: 30_000,
  })
}

/** 12(改). カテゴリ割当 保存/削除(CM2=ブラウズノード単位 / CM3=ASIN単位) */
export function useSaveMallCategoryMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ asin, req }: { asin: string; req: SaveMallCategoryRequest }) =>
      api.saveMallCategoryMapping(asin, req),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mallCategory(variables.asin) })
    },
  })
}

/** 13a. 商品画像 追加 */
export function useAddProductImageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ asin, req }: { asin: string; req: AddProductImageRequest }) =>
      api.addProductImage(asin, req),
    onSuccess: (_data, variables) => {
      markImagesEdited(variables.asin)
      queryClient.invalidateQueries({ queryKey: queryKeys.productImages(variables.asin) })
    },
  })
}

/**
 * 13c. 商品画像 アップロード(ファイル選択→S3直PUT→メタデータ登録) [フェーズ2]
 * VB版 ButtonImageReplace の「ローカルjpg→FTP→DB登録」に相当する一連の流れを
 * 1つのmutationにまとめる。
 *   1) getImageUploadUrl で署名付きURL + 採番済ImageNo + 公開URL を取得
 *   2) uploadFileToS3 で実ファイルをS3へPUT
 *   3) addProductImage で amazon.image に登録(Upsert)
 */
export function useUploadProductImageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      asin,
      file,
      regionCode,
      imageTypeId,
    }: {
      asin: string
      file: File
      regionCode: string
      imageTypeId?: number
    }) => {
      const contentType = file.type || 'image/jpeg'
      // 1) 署名付きURL + 採番情報を取得
      const issued = await api.getImageUploadUrl(asin, { regionCode, imageTypeId, contentType })
      // 2) S3へ直PUT
      await api.uploadFileToS3(issued.uploadUrl, file, contentType)
      // 3) メタデータ登録(発行時に採番されたimageNo/公開URLをそのまま使う)
      return api.addProductImage(asin, {
        imageUrl: issued.imageUrl,
        regionCode: issued.regionCode,
        imageTypeId: issued.imageTypeId,
        imageNo: issued.imageNo,
      })
    },
    onSuccess: (_data, variables) => {
      markImagesEdited(variables.asin)
      queryClient.invalidateQueries({ queryKey: queryKeys.productImages(variables.asin) })
    },
  })
}

/** 13b. 商品画像 削除 */
export function useDeleteProductImageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      asin,
      imageNo,
      regionCode,
      imageTypeId,
    }: {
      asin: string
      imageNo: number
      regionCode: string
      imageTypeId?: number
    }) => api.deleteProductImage(asin, imageNo, regionCode, imageTypeId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productImages(variables.asin) })
    },
  })
}

/** 13d. 商品画像 並び替え(前へ/後へ) [フェーズ3] */
export function useMoveProductImageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      asin,
      imageNo,
      req,
    }: {
      asin: string
      imageNo: number
      req: MoveProductImageRequest
    }) => api.moveProductImage(asin, imageNo, req),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productImages(variables.asin) })
    },
  })
}

/** 13e. 商品画像 更新(SP-API Catalog Itemsから再取得) [フェーズ5] */
export function useRefreshProductImageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      asin,
      req,
    }: {
      asin: string
      req: RefreshProductImageRequest
    }) => api.refreshProductImage(asin, req),
    onSuccess: (_data, variables) => {
      markImagesEdited(variables.asin)
      queryClient.invalidateQueries({ queryKey: queryKeys.productImages(variables.asin) })
    },
  })
}

/** 画像一括処理 [ハッシュ完全一致検索] */
export function useSearchImagesByHashMutation() {
  return useMutation({
    mutationFn: ({
      asin,
      imageNo,
      regionCode,
      siblingsOnly,
    }: {
      asin: string
      imageNo: number
      regionCode: string
      siblingsOnly: boolean
    }) => api.searchImagesByHash(asin, imageNo, regionCode, siblingsOnly),
  })
}

/** 画像一括処理 [一時アップロードURL発行] */
export function useBulkUploadStagingUrlMutation() {
  return useMutation({
    mutationFn: (contentType: string) => api.getBulkUploadStagingUrl(contentType),
  })
}

/** 画像一括処理 [一括削除] */
export function useBulkDeleteImagesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: BulkDeleteImagesRequest) => api.bulkDeleteImages(req),
    onSuccess: (_data, variables) => {
      for (const t of variables.targets) {
        queryClient.invalidateQueries({ queryKey: queryKeys.productImages(t.asin) })
      }
    },
  })
}

/** 画像一括処理 [一括置換] */
export function useBulkReplaceImagesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: BulkReplaceImagesRequest) => api.bulkReplaceImages(req),
    onSuccess: (_data, variables) => {
      for (const t of variables.targets) {
        markImagesEdited(t.asin)
        queryClient.invalidateQueries({ queryKey: queryKeys.productImages(t.asin) })
      }
    },
  })
}

/**
 * 関連ファイル数バッジ [親ASIN / 子ASIN / セラー]
 *
 * 値は FileCounter.exe が bat 実行時にDBへ書いたスナップショットなので、
 * 画面を開いた時点(および商品を切り替えた時点)に取得するだけでよい。
 * リンククリックに連動した再取得は行わない。
 */
export function useRelatedFileCounts(
  parentAsin: Ref<string | null>,
  childAsin: Ref<string | null>,
  sellerId: Ref<string | null>,
) {
  return useQuery({
    queryKey: ['relatedFileCounts', parentAsin, childAsin, sellerId],
    queryFn: () => api.getRelatedFileCounts(parentAsin.value, childAsin.value, sellerId.value),
    enabled: computed(() => !!(parentAsin.value || childAsin.value || sellerId.value)),
  })
}

/** 画像一括処理 [兄弟ASINへの一括追加・実行前プレビュー] */
export function useSiblingProductsMutation() {
  return useMutation({
    mutationFn: ({
      asin,
      regionCode,
      parentAsin,
    }: {
      asin: string
      regionCode: string
      parentAsin?: string | null
    }) => api.getSiblingProducts(asin, regionCode, parentAsin),
  })
}

/** 画像一括処理 [兄弟ASINへの一括追加] */
export function useBulkAddSiblingImagesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: BulkAddSiblingImagesRequest) => api.bulkAddSiblingImages(req),
    onSuccess: (data) => {
      for (const asin of data.targetAsins) {
        markImagesEdited(asin)
        queryClient.invalidateQueries({ queryKey: queryKeys.productImages(asin) })
      }
    },
  })
}

/** ジャッジ待ち申請(VB版 UncheckPicker.exe GUIモード相当) */
export function useApplyJudgmentQueueMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: ApplyJudgmentQueueRequest) => api.applyJudgmentQueue(req),
    onSuccess: () => {
      // ステータスが変わった商品があるため、一覧・詳細を再取得する
      queryClient.invalidateQueries({ queryKey: ['offers'] })
      queryClient.invalidateQueries({ queryKey: ['offerDetail'] })
    },
  })
}

// ---- ユーザ管理(Cognito、Adminグループのみ利用可) ----------------------------

/** ユーザ一覧(キーワード・グループ・有効/無効でフィルタ) */
export function useUsers(keyword: Ref<string>, group: Ref<string>, status: Ref<string>) {
  return useQuery({
    queryKey: computed(() => queryKeys.users(keyword.value, group.value, status.value)),
    queryFn: () =>
      api.getUsers({
        keyword: keyword.value || undefined,
        group: group.value || undefined,
        status: status.value || undefined,
      }),
  })
}

export function useCreateUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateUserRequest) => api.createUser(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useUpdateUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ username, req }: { username: string; req: UpdateUserRequest }) =>
      api.updateUser(username, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useSetUserStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ username, req }: { username: string; req: SetUserStatusRequest }) =>
      api.setUserStatus(username, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

// ---- 画像編集(画像編集の作り直し) ---------------------------------------------

/** 画像編集画面: 「登録する画像」の並びで一括確定 */
export function useSaveProductImagesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ asin, req }: { asin: string; req: SaveProductImagesRequest }) =>
      api.saveProductImages(asin, req),
    onSuccess: (_data, variables) => {
      markImagesEdited(variables.asin)
      queryClient.invalidateQueries({ queryKey: queryKeys.productImages(variables.asin) })
    },
  })
}
